import { FileText, Lock, ExternalLink, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

export function ResearchSection({ research, loading, emptyMessage }) {
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <section 
            id="research" 
            className="py-16 sm:py-24 bg-background"
            data-testid="research-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="mb-12 sm:mb-16">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-8 h-8 bg-primary flex items-center justify-center">
                            <FileText className="h-4 w-4 text-primary-foreground" />
                        </div>
                        <h2 
                            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight uppercase"
                            style={{ fontFamily: "'Silkscreen', cursive" }}
                            data-testid="research-title"
                        >
                            Research
                        </h2>
                    </div>
                    <p className="text-muted-foreground max-w-2xl">
                        In-depth research and analysis. Request access to view the full documents.
                    </p>
                </div>

                {/* Scrollable Research Grid */}
                <div className="max-h-[600px] overflow-y-auto pr-2 scrollbar-thin">
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array(3).fill(0).map((_, i) => (
                                <div key={i} className="retro-card p-6">
                                    <div className="h-6 w-3/4 bg-muted skeleton-pulse mb-3" />
                                    <div className="h-4 w-full bg-muted skeleton-pulse mb-2" />
                                    <div className="h-4 w-2/3 bg-muted skeleton-pulse" />
                                </div>
                            ))}
                        </div>
                    ) : research.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {research.map((item) => (
                                <Link 
                                    key={item.id}
                                    to={`/research/${item.id}`}
                                    className="block"
                                >
                                    <article 
                                        className="retro-card group p-6 hover:border-primary transition-all cursor-pointer relative h-full"
                                        data-testid={`research-card-${item.id}`}
                                    >
                                        {/* Lock icon overlay */}
                                        <div className="absolute top-4 right-4 w-8 h-8 bg-muted/80 flex items-center justify-center border-2 border-border">
                                            <Lock className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        
                                        <h3 
                                            className="text-lg sm:text-xl tracking-tight mb-3 pr-12 group-hover:text-primary transition-colors"
                                            style={{ fontFamily: "'Silkscreen', cursive" }}
                                        >
                                            {item.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm line-clamp-3">
                                            {item.excerpt || 'Click to preview and request access to this research.'}
                                        </p>
                                        
                                        {/* Published Date */}
                                        <div className="flex items-center gap-1 mt-3 text-[10px] text-muted-foreground uppercase tracking-widest">
                                            <Calendar className="h-3 w-3" />
                                            {formatDate(item.created_at)}
                                        </div>
                                        
                                        <div className="mt-4 flex items-center gap-2 text-primary text-xs uppercase tracking-widest">
                                            <span>Preview & Request Access</span>
                                            <ExternalLink className="h-3 w-3" />
                                        </div>
                                    </article>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="retro-card p-12 text-center">
                            <FileText className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                            <p 
                                className="text-muted-foreground italic text-sm"
                                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                                data-testid="research-empty-message"
                            >
                                {emptyMessage || 'Research coming soon. Check back later!'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
